import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { StatusBadge, ApplicationStatus } from './StatusBadge';
import { ScoreDisplay } from './ScoreDisplay';
import { FiAlertCircle } from 'react-icons/fi';
import { Button } from '@/components/common';

export interface ApplicationCardData {
  id: string | number;
  housing_type_name: string;
  organization_name?: string;
  adresse?: string;
  status: ApplicationStatus;
  score?: number | null;
  submitted_at?: string;
  dateSubmitted?: string;
  nextStep?: string;
}

interface ApplicationCardProps {
  application: ApplicationCardData;
  onViewDetails?: () => void;
  onEdit?: () => void;
  showProgress?: boolean;
}

const CardContainer = styled.div`
  background: white;
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.md};
  transition: all ${theme.transitions.normal};
  cursor: pointer;

  &:hover {
    border-color: ${theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.md};
  gap: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const CardTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const CardSubtitle = styled.p`
  color: ${theme.colors.text}80;
  font-size: ${theme.typography.sizes.sm};
  margin: ${theme.spacing.xs} 0;
`;

const CardMeta = styled.p`
  color: ${theme.colors.text}60;
  font-size: ${theme.typography.sizes.xs};
  margin: 0;
`;

const NextStepAlert = styled.div`
  background: ${theme.colors.info}10;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.info};
  font-size: ${theme.typography.sizes.sm};

  svg {
    flex-shrink: 0;
    font-size: 18px;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  margin: ${theme.spacing.md} 0;
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${props => props.$progress}%;
  background: ${theme.colors.primary};
  transition: width ${theme.transitions.slow};
`;

const CardActions = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const ScoreContainer = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

const getProgressPercentage = (status: ApplicationStatus): number => {
  const progressMap: Record<ApplicationStatus, number> = {
    draft: 10,
    pending: 30,
    submitted: 30,
    verification: 50,
    in_review: 50,
    scored: 70,
    approved: 100,
    rejected: 100,
  };
  return progressMap[status] || 0;
};

export const ApplicationCard: React.FC<ApplicationCardProps> = ({
  application,
  onViewDetails,
  onEdit,
  showProgress = true,
}) => {
  const displayedDate =
    application.submitted_at || application.dateSubmitted;

  return (
    <CardContainer onClick={onViewDetails}>
      <CardHeader>
        <div style={{ flex: 1 }}>
          <CardTitle>{application.housing_type_name}</CardTitle>
          {application.adresse && (
            <CardSubtitle>{application.adresse}</CardSubtitle>
          )}
          {application.organization_name && (
            <CardSubtitle>{application.organization_name}</CardSubtitle>
          )}
          {displayedDate && (
            <CardMeta>
              Soumise le {new Date(displayedDate).toLocaleDateString('fr-FR')}
            </CardMeta>
          )}
        </div>
        <StatusBadge status={application.status} size="md" />
      </CardHeader>

      {application.score !== null && application.score !== undefined && (
        <ScoreContainer>
          <ScoreDisplay score={application.score} variant="inline" />
        </ScoreContainer>
      )}

      {application.nextStep && (
        <NextStepAlert>
          <FiAlertCircle />
          <span>{application.nextStep}</span>
        </NextStepAlert>
      )}

      {showProgress && (
        <ProgressBar>
          <ProgressFill $progress={getProgressPercentage(application.status)} />
        </ProgressBar>
      )}

      <CardActions onClick={e => e.stopPropagation()}>
        {onViewDetails && (
          <Button variant="primary" onClick={onViewDetails}>
            Voir les détails
          </Button>
        )}
        {application.status === 'draft' && onEdit && (
          <Button variant="secondary" onClick={onEdit}>
            Finaliser la candidature
          </Button>
        )}
      </CardActions>
    </CardContainer>
  );
};
